/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import GridItem from '../../../components/Grid/GridItem';
import GridContainer from '../../../components/Grid/GridContainer';
import { Fraction } from '../../../interfaces/platform/Fraction';
import AddButton from '../../../components/Buttons/AddButton';
import RemoveButton from '../../../components/Buttons/RemoveButton';
import Pager from '../../../components/Adescom/Pager';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { fractionsConstants } from '../../../modules/fractions/actions';
import { FractionCollectionInterface } from '../../../interfaces/collections/FractionCollectionInterface';
import { useDispatch } from 'react-redux';
import { DiscountEntity } from '../PlatformResellerDiscount/PlatformResellerDiscount';
import { DiscountRateEntity } from '../PlatformResellerDiscount/PlatformResellerRateDiscount';

interface FractionSelectionProps {
    getSelectedFractions: (fractions: Array<Fraction>) => void;
    entity?: DiscountEntity | DiscountRateEntity;
}

const FractionSelection = (props: FractionSelectionProps): React.ReactElement => {
    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();
    const useReducer = false;

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    const [editEntity, setEditEntity] = useState<DiscountEntity | DiscountRateEntity | null>(null);
    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [fractions, setFractions] = useState<FractionCollectionInterface | null>(null);
    const [selectedFractions, setSelectedFractions] = useState<Array<Fraction>>(
        props.entity && Array.isArray(props.entity.destination) ? props.entity.destination : []
    );

    const paginationCallbackFunction = useCallback(
        (uri: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath('/fractions');
            }

            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                fractionsConstants.LIST,
                filters,
                orders,
                (data: FractionCollectionInterface | null) => {
                    if (!useReducer) {
                        setFractions(data);
                    }
                },
                { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
            );
        },
        [dispatch, filters, orders, useReducer]
    );

    const selectFraction = (params: any): void => {
        const newFractions = [...selectedFractions, { ...params.fraction }];
        setSelectedFractions(newFractions);
        props.getSelectedFractions(newFractions);
    };

    const unselectFraction = (params: Fraction): void => {
        let newArr = [...selectedFractions];
        selectedFractions.forEach((el: Fraction, indx: number) => {
            if (el.id === params.fraction.id) {
                newArr.splice(indx, 1);
            }
        });
        setSelectedFractions(newArr);
        props.getSelectedFractions(newArr);
    };

    const updateOrders = (orders: Array<Order>): void => {
        setOrders(orders);
    };
    const updateFilters = (filters: Array<Filter>): void => {
        setFilters(filters);
    };

    useEffect(() => {
        const uri = FormGeneralHelper.getPath('/fractions');
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            fractionsConstants.LIST,
            filters,
            orders,
            (data: FractionCollectionInterface | null) => {
                if (!useReducer) {
                    setFractions(data);
                }
            },
            { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
        );
    }, [dispatch, filters, orders, useReducer]);

    // useEffect(() => {
    //     if (
    //         selectedFractions.length === 0 &&
    //         props.entity?.destination &&
    //         props.entity.destination.length > 0 &&
    //         Array.isArray(props.entity.destination)
    //     ) {
    //         setSelectedFractions(props.entity.destination);
    //     }
    // }, [selectedFractions, props]);

    useEffect(() => {
        if (
            props.entity &&
            props.entity.destination &&
            Array.isArray(props.entity.destination) &&
            (!editEntity || (editEntity && editEntity.name !== props.entity.name))
        ) {
            setEditEntity(props.entity);
            setSelectedFractions(props.entity?.destination);
        }
        if (!props.entity || props.entity.destination === null) {
            setSelectedFractions([]);
        }
    }, [props.entity, editEntity]);

    return (
        <GridContainer style={{ marginTop: 25 }}>
            <GridItem xs={6}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyHeader} align="left" style={{ width: 40 }}>
                                <TableHeaderContentCell textAlign="center" title="clients:c.commands" />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="center"
                                    title="discount:h.name"
                                    updateOrderList={updateOrders}
                                    updateFilterList={updateFilters}
                                    fieldName="mainname"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fractions &&
                            fractions['hydra:member'].map((el: Fraction) => {
                                return (
                                    <TableRow key={'to_select_' + el.id}>
                                        <TableCell classes={tableCellRoot}>
                                            {
                                                <AddButton
                                                    callback={selectFraction}
                                                    callbackParam={{ fraction: el }}
                                                    disabled={selectedFractions.some((sf: { id: number; mainname: string }) => {
                                                        return sf.id === el.id;
                                                    })}
                                                />
                                            }
                                        </TableCell>
                                        <TableCell classes={tableCellRoot}>{el.mainname}</TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell classes={tableCellRoot} colSpan={4}>
                                {fractions && fractions['hydra:view'] && (
                                    <Pager callback={paginationCallbackFunction} views={fractions['hydra:view']} />
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </GridItem>
            <GridItem xs={6}>
                <div style={{ maxHeight: 822, overflowY: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyHeader} align="left" style={{ width: 40 }}>
                                    <TableHeaderContentCell textAlign="center" title="clients:c.commands" />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="center"
                                        title="discount:h.name"
                                        updateOrderList={updateOrders}
                                        updateFilterList={updateFilters}
                                        fieldName="mainname"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedFractions &&
                                selectedFractions.map((el: { id: number; mainname: string }) => {
                                    return (
                                        <TableRow key={'selected_' + el.id}>
                                            <TableCell classes={tableCellRoot}>
                                                {
                                                    <RemoveButton
                                                        callback={unselectFraction}
                                                        callbackParam={{ fraction: el }}
                                                        disabled={
                                                            !selectedFractions.some((sf: { id: number; mainname: string }) => {
                                                                return sf.id === el.id;
                                                            })
                                                        }
                                                    />
                                                }
                                            </TableCell>
                                            <TableCell classes={tableCellRoot}>{el.mainname}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </div>
            </GridItem>
        </GridContainer>
    );
};

export default FractionSelection;
